import React, { useEffect } from 'react';
import { Outlet, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_SEO_LOCALES } from '../component/seo/constants';
// Imported from ./locales rather than ../i18n so this module does not pull
// react-i18next's real implementation into suites that mock it. See ../i18n/locales.
import { changeLocale } from '../i18n/locales';

const LANG_TO_I18N: Record<string, string> = Object.fromEntries(
  Object.entries(SUPPORTED_SEO_LOCALES).map(([k, v]) => [k, v.i18nKey])
);

export const LocaleLayout: React.FC = () => {
  const { lang } = useParams<{ lang?: string }>();
  const { i18n } = useTranslation();
  const targetI18n = lang ? LANG_TO_I18N[lang] : undefined;

  /*
   * Follow the URL's locale prefix — but only when there is one.
   *
   * Goes through changeLocale rather than i18n.changeLanguage because catalogues are
   * fetched on demand: switching the language before its messages exist would render
   * the page in English first and in the right language a moment later. On a
   * prerendered load this effect is a no-op — src/index.tsx has already resolved the
   * language from the same URL and loaded it — so the cost lands only on client-side
   * navigation into a locale, which is where the fetch is unavoidable anyway.
   *
   * This used to fall back to forcing en-US whenever the path carried no prefix,
   * which made it impossible to select any of the ~48 locales that have no URL of
   * their own: the menu switched the language and this effect put it straight back.
   * That fallback was also redundant. getInitialLocale() in src/i18n already pins the
   * language to en-US for any load it is going to hydrate, precisely so the first
   * render matches the English snapshot — so nothing here is holding hydration
   * together. All the fallback still did was overrule a choice the visitor made
   * after the page was interactive, which is not this component's business.
   */
  useEffect(() => {
    if (targetI18n && i18n.language !== targetI18n) {
      void changeLocale(targetI18n);
    }
  }, [targetI18n, i18n]);

  if (lang && !(lang in SUPPORTED_SEO_LOCALES)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};
